// Economic-group resolution — collapse legal entities to their ultimate
// operating group so concentration metrics (CR5 / HHI / independent count) are
// computed on real competitors, not on individual companies. A buy-and-build
// roll-up (IVC, Vets4Pets, mydentist…) appears in the registry as dozens of
// separate legal entities; counted individually they vanish from the top-5 and
// the market reads far more fragmented than it is.
//
// Two resolution paths, both precision-first:
//   1. Via corporate_parent — resolveSponsor() already maps a resolved parent
//      to a named operator (the Sponsor-Ownership reference). Reused verbatim.
//   2. Via the entity's OWN name — many roll-ups brand their opcos ("Abingdon
//      Vets4Pets Ltd", "Companion Care (Crawley) Ltd"). We match a CURATED set
//      of distinctive brand tokens/phrases anywhere in the name. This is an
//      allowlist, NOT a heuristic: only hand-vetted, unambiguous brand terms
//      are listed, because this feeds a headline metric and a false positive
//      ("Animal Doctors" → a lab group) would corrupt CR5. Grow it deliberately
//      and re-validate against the live registry (zero-FP bar).
//
// resolveEconomicGroup returns the group name (an operator) or null = the
// company is its own economic unit (genuinely independent / unattributed).

import { resolveSponsor, normaliseHoldco } from './sponsor-ownership.js';

export const ECONOMIC_GROUP_BRANDS_VERSION = '2026-06-19';

// Curated distinctive brand terms → operator (the SPONSOR_OPERATORS name).
// Single tokens must be unmistakable (a generic word like "smile"/"doctors"/
// "care" is NEVER listed); multi-word phrases are matched contiguously. Keyed
// by operator so the group name stays consistent with the Backing tag.
// UK-first: seeded with the operators that demonstrably brand their registry
// entities. Validated zero-FP against the live GB registry on 2026-06-12.
const BRAND_TERMS: Readonly<Record<string, ReadonlyArray<string>>> = {
  'Pets at Home / Vets4Pets': ['vets4pets', 'companion care'],
  Medivet: ['medivet'],
  'IVC Evidensia': ['evidensia', 'independent vetcare'],
  'CVS Group': ['cvs vets', 'companion care veterinary'],
  mydentist: ['mydentist'],
  PortmanDentex: ['portman dental', 'dentex'],
  'Rodericks Dental Partners': ['rodericks dental'],
  'Colosseum Dental Group': ['colosseum dental'],
  'Together Dental': ['together dental'],
  Newmedica: ['newmedica'],
  'Optical Express': ['optical express'],
  'Community Health and Eyecare': ['community health and eyecare'],
  Specsavers: ['specsavers'],
  Cera: ['cera care', 'first city nursing'],
  'Priory Group': ['priory healthcare', 'priory adult care'],
  Aspris: ['aspris'],
  CareTech: ['caretech'],
  'Voyage Care': ['voyage care'],
  'Lifeways Group': ['lifeways'],
  'Keys Group': ['keys group'],
  'Achieve together': ['achieve together'],
  // UK pharma wholesale — the three national full-line wholesalers, added
  // 2026-06-19. corporate_parent is empty for all three (and they carry no
  // financials yet), so these distinctive contiguous phrases on the opco's OWN
  // name are the attribution route; keys match the sponsor-crosswalk operator
  // names so the Backing tag (resolveSponsor → AURELIUS / Cencora / PHOENIX)
  // stays consistent. AAH = AURELIUS (NOT Phoenix — the 2022 Phoenix/McKesson
  // deal excluded the UK). Phoenix wholesale is kept DISTINCT from the unrelated
  // "Phoenix Hospital Group" / "Phoenix Surgery". Verified zero-FP against the
  // live GB registry (each phrase hits only its one wholesaler entity).
  'Hallo Healthcare Group': ['aah pharmaceuticals'],
  'Alliance Healthcare': ['alliance healthcare distribution'],
  'Phoenix Group (UK wholesale)': ['phoenix healthcare distribution', 'phoenix medical supplies'],
  // France — curated quick-win 2026-06-13. FR has no PSC-style ownership chain
  // (corporate_parent is empty for every FR row), so these distinctive brand
  // tokens on the opco's OWN name are the only attribution route until the INPI
  // formalités parent-link is ingested. Verified zero-FP against the live FR+GB
  // registry (the only GB hit is UNILABS LIMITED = correctly Unilabs). Keys are
  // the crosswalk operator names so resolveSponsor finds the backer. Generic
  // single words avoided — multi-token phrases where the token is common.
  'Cerba HealthCare': ['cerba', 'cerballiance'],
  'Groupe Inovie': ['inovie'],
  // ⚠️ The bare `synlab` token maps to the PAN-EUROPEAN parent, never to a
  // country sub-group. It was 'Synlab France' until 2026-09-09 — a slip from
  // this France-only batch, which was verified against the FR+GB registry when
  // those were the only markets in play. Across 27 markets the token matches 193
  // entities in 11 countries and only 24 are French, so it was labelling Spanish,
  // Italian and German labs as a French group. 'Synlab France' is still a real
  // operator (sponsor-data.ts) with its own specific aliases — Biofrance,
  // Provence, Nouvelle-Aquitaine — and corporate_parent resolves FIRST, so an
  // entity whose parent genuinely is the French sub-group still lands there.
  // This entry is only the name-based fallback, and the safe answer for a
  // brand token that spans a continent is the parent.
  'SYNLAB Group': ['synlab'],
  Biogroup: ['biogroup'],
  Unilabs: ['unilabs'],
  'Eurofins (clinical Dx)': ['eurofins biologie', 'eurofins labazur', 'eurofins biomnis'],
  Audilab: ['audilab'],
  'Point Vision': ['point vision'],
  'Alain Afflelou': ['afflelou'],
  'Optic 2000 Group': ['optic 2000'],
  'Optical Center': ['optical center'],
  'Krys Group': ['krys'],
  Lazeo: ['lazeo'],
  Elsan: ['elsan'],
  DomusVi: ['domusvi'],
  emeis: ['emeis'],
  Inicea: ['inicea'],
  Univet: ['univet'],
  Amplifon: ['amplifon'],
  VivaSon: ['vivason'],
  Imadis: ['imadis'],
  'Emera Group': ['emera'],
};

interface BrandPhrase {
  tokens: string[];
  group: string;
}
const BRAND_PHRASES: ReadonlyArray<BrandPhrase> = (() => {
  const out: BrandPhrase[] = [];
  for (const [group, terms] of Object.entries(BRAND_TERMS)) {
    for (const term of terms) {
      const tokens = normaliseHoldco(term).split(' ').filter(Boolean);
      if (tokens.length) out.push({ tokens, group });
    }
  }
  // Longest phrase first → most specific wins.
  return out.sort((a, b) => b.tokens.length - a.tokens.length);
})();

/** True if `needle` is a contiguous run inside `haystack`. */
function contains(haystack: string[], needle: string[]): boolean {
  if (needle.length === 0 || needle.length > haystack.length) return false;
  outer: for (let i = 0; i + needle.length <= haystack.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return true;
  }
  return false;
}

/** Match a company's own name against the curated brand allowlist. */
export function matchBrand(legalName: string | null | undefined): string | null {
  if (!legalName) return null;
  const tokens = normaliseHoldco(legalName).split(' ').filter(Boolean);
  for (const { tokens: phrase, group } of BRAND_PHRASES) {
    if (contains(tokens, phrase)) return group;
  }
  return null;
}

export interface EconomicGroup {
  group: string;
  basis: 'parent' | 'brand';
}

/**
 * Resolve a company to its economic group. corporate_parent (via the ownership
 * reference) first, then the curated brand allowlist on the company's own name.
 * null = its own economic unit (independent / unattributed).
 */
export function resolveEconomicGroup(
  legalName: string | null | undefined,
  corporateParent: string | null | undefined,
): EconomicGroup | null {
  const viaParent = resolveSponsor(corporateParent);
  if (viaParent?.operator) return { group: viaParent.operator, basis: 'parent' };

  const viaBrand = matchBrand(legalName);
  if (viaBrand) return { group: viaBrand, basis: 'brand' };

  return null;
}

// ── Consolidation-duplicate detection ───────────────────────────────────────
// A group that files CONSOLIDATED accounts often appears in the registry as a
// holding company AND its operating child, BOTH carrying the same group revenue
// (the parent consolidates the child). Counted separately they double the
// group's revenue — inflating market totals and CR5/HHI (e.g. SDC Group £21.4M +
// SDC Clinics £21.4M = a phantom £42.8M). This detects the redundant HOLDCO so
// it can be dropped from the competitive universe (keep the operating child).
//
// Signature — deliberately narrow to avoid suppressing a real operator:
//   • the holdco has NO regulated site (a holding vehicle, not an operator);
//   • ≥1 OTHER in-scope entity names it as corporate_parent;
//   • its revenue ≈ the SUM of those children's revenue (same accounts twice).
// Revenues are compared in the filing currency (ratio is FX-invariant), so a
// raw filing figure is fine. Returns the holdco ids to suppress.

/** Light name normalisation for parent↔name matching: strip legal suffixes +
 *  punctuation + case, but KEEP distinctive words (Holdings/Group) so
 *  "SDC Group Limited" still matches a corporate_parent of "Sdc Group Limited". */
export function normaliseCompanyName(s: string | null | undefined): string {
  return (s ?? '')
    .toLowerCase()
    .replace(/[.,]/g, ' ')
    .replace(/\b(limited|ltd|llp|plc|the|uk)\b/g, ' ')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface DuplicateCandidate {
  id: string;
  name: string | null;
  corporateParent: string | null;
  /** Revenue in the filing currency (FX-invariant ratio — raw figure is fine). */
  revenue: number | null;
  /** Count of regulated sites attributed to the entity (0 ⇒ holding vehicle). */
  siteCount: number;
}

/**
 * Detect consolidation-duplicate holdcos across a set of in-scope entities.
 * Returns the set of holdco `id`s that duplicate revenue already counted at
 * their operating children — suppress these from the competitive universe.
 * `tolerance` = allowed |parent − Σchildren| / parent (default 10%).
 */
export function detectConsolidationDuplicates(
  rows: readonly DuplicateCandidate[],
  tolerance = 0.1,
): Set<string> {
  // Children indexed by normalised corporate_parent.
  const childrenByParent = new Map<string, DuplicateCandidate[]>();
  for (const r of rows) {
    if (!r.corporateParent) continue;
    const k = normaliseCompanyName(r.corporateParent);
    if (!k) continue;
    const list = childrenByParent.get(k);
    if (list) list.push(r);
    else childrenByParent.set(k, [r]);
  }
  const dup = new Set<string>();
  for (const p of rows) {
    if (p.siteCount !== 0) continue; // an operator with premises is not a pure holdco
    if (!p.revenue || p.revenue <= 0) continue;
    const pn = normaliseCompanyName(p.name);
    if (!pn) continue;
    const kids = (childrenByParent.get(pn) ?? []).filter(
      (k) => k.id !== p.id && normaliseCompanyName(k.name) !== pn,
    );
    if (kids.length === 0) continue;
    const sumKids = kids.reduce((s, k) => s + (k.revenue ?? 0), 0);
    if (sumKids > 0 && Math.abs(p.revenue - sumKids) / p.revenue < tolerance) dup.add(p.id);
  }
  return dup;
}
